import React, { useEffect, useId, useMemo, useState } from 'react';

import { warehouseApi } from 'api/warehouse';
import { Button } from 'shadcn/components/ui/Button';
import { Checkbox } from 'shadcn/components/ui/Checkbox';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shadcn/components/ui/Select';

import { BULK_MOVE_SEARCH_FIELDS, isBulkMoveSearchFieldExact } from './bulkMovePalletFilters';

function BulkMovePalletActionBar({
    destinationLocation,
    expandAll,
    facilityID,
    isLoading = false,
    onDestinationLocationChange,
    onExpandAllChange,
    onMove,
    onRecipeCardsOnlyChange,
    onSearchByChange,
    onSearchTermChange,
    pallets,
    recipeCardsOnly,
    searchBy,
    searchTerm,
    selectedCount,
}) {
    const recipeCardsOnlyId = useId();
    const expandAllId = useId();
    const [availableLocations, setAvailableLocations] = useState([]);
    const [loadingLocations, setLoadingLocations] = useState(false);

    const searchPlaceholder = useMemo(() => {
        const field = BULK_MOVE_SEARCH_FIELDS.find((option) => option.id === searchBy);
        return field?.placeholder ?? 'Enter search term';
    }, [searchBy]);

    const hasActiveSearch = searchTerm.trim().length > 0;

    const handleClearSearch = () => {
        onSearchTermChange('');
    };

    // Load available locations for bulk move
    useEffect(() => {
        const fetchLocations = async () => {
            setLoadingLocations(true);
            const response = await warehouseApi.getBulkMoveEnabledLocations(facilityID);
            if (response.status && response.data?.rows?.length > 0) {
                const locationOptions = response.data.rows
                    .map((location) => ({
                        label: location.name,
                        value: location.id.toString(),
                        name: location.name,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label));

                requestAnimationFrame(() => {
                    setAvailableLocations(locationOptions);
                });
            } else {
                setAvailableLocations([]);
            }
            setLoadingLocations(false);
        };

        if (facilityID) {
            fetchLocations();
        }
    }, [facilityID]);

    const handleLocationChange = (locationId) => {
        const selectedLocation = availableLocations.find((loc) => loc.value === locationId);
        onDestinationLocationChange(locationId, selectedLocation?.name || '');
    };

    return (
        <div className="w-full min-w-0 border-b px-6 py-4">
            <div className="flex flex-col gap-3">
                <div className="flex min-w-0 flex-wrap items-center gap-3">
                    <Select value={searchBy} onValueChange={onSearchByChange}>
                        <SelectTrigger className="w-[180px] shrink-0">
                            <SelectValue placeholder="Search by" />
                        </SelectTrigger>
                        <SelectContent position="popper" sideOffset={4} align="start">
                            {BULK_MOVE_SEARCH_FIELDS.map((field) => (
                                <SelectItem key={field.id} value={field.id}>
                                    {field.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Input
                        placeholder={searchPlaceholder}
                        value={searchTerm}
                        onChange={(e) => onSearchTermChange(e.target.value)}
                        className="w-full min-w-[180px] max-w-[220px]"
                        inputMode={isBulkMoveSearchFieldExact(searchBy) ? 'numeric' : 'text'}
                    />
                    {hasActiveSearch ? (
                        <Button
                            type="button"
                            variant="link"
                            className="h-auto shrink-0 p-0 text-destructive hover:text-destructive/90"
                            onClick={handleClearSearch}
                            aria-label="Clear search"
                        >
                            Clear
                        </Button>
                    ) : null}
                </div>
                <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-4 gap-y-3">
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id={recipeCardsOnlyId}
                                checked={recipeCardsOnly}
                                onCheckedChange={(checked) => onRecipeCardsOnlyChange(checked === true)}
                            />
                            <Label
                                className="cursor-pointer whitespace-nowrap text-sm font-normal"
                                htmlFor={recipeCardsOnlyId}
                            >
                                Recipe cards only
                            </Label>
                        </div>
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id={expandAllId}
                                checked={expandAll}
                                disabled={isLoading || pallets.length === 0}
                                onCheckedChange={(checked) => onExpandAllChange(checked === true)}
                            />
                            <Label className="cursor-pointer whitespace-nowrap text-sm font-normal" htmlFor={expandAllId}>
                                Expand all
                            </Label>
                        </div>
                        <span className="whitespace-nowrap text-sm text-gray-600">
                            {selectedCount} of {pallets.length} selected
                        </span>
                    </div>
                    <div className="flex min-w-0 flex-wrap items-center gap-3">
                        <span className="whitespace-nowrap text-sm text-gray-600">Bulk move to:</span>
                        <Select
                            value={destinationLocation}
                            onValueChange={handleLocationChange}
                            disabled={loadingLocations}
                        >
                            <SelectTrigger className="w-48 max-w-full shrink-0">
                                <SelectValue placeholder="Select location" />
                            </SelectTrigger>
                            <SelectContent
                                position="popper"
                                sideOffset={4}
                                className="max-h-[300px] overflow-auto"
                                align="start"
                            >
                                {availableLocations.length === 0 ? (
                                    <div className="p-2 text-center text-sm text-gray-500">
                                        {loadingLocations ? 'Loading locations...' : 'No locations available'}
                                    </div>
                                ) : (
                                    availableLocations.map((location) => (
                                        <SelectItem key={location.value} value={location.value}>
                                            {location.label}
                                        </SelectItem>
                                    ))
                                )}
                            </SelectContent>
                        </Select>
                        <Button
                            variant="default"
                            className="shrink-0"
                            disabled={!destinationLocation || selectedCount === 0 || isLoading}
                            onClick={onMove}
                        >
                            Confirm Move
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export { BulkMovePalletActionBar };
